import {
  Business,
  Edit,
  Email,
  ExpandLess,
  ExpandMore,
  Person,
  Phone,
  Visibility,
} from '@mui/icons-material';
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavigation } from '../../context/NavigationContext';
import { InvestmentEntity } from '../../models/InvestmentEntity';
import InvestmentEntityContact from '../../models/InvestmentEntityContact';
import { Palette } from 'Palette';
import { StyledAvatar, StyledCard, StyledCardContent } from '../styles/styles';

interface ExpandableEntityCardProps {
  entity: InvestmentEntity;
  tone?: keyof typeof Palette;
  expanded?: boolean;
  onExpandedChange?: (expanded: boolean) => void;
  contacts: InvestmentEntityContact[] | undefined;
  /** show spinner in expanded area until contacts are loaded */
  contactsLoading?: boolean;
  /** OPTIONAL: if provided, that contact is shown first, highlighted,
   *  and its "View Contact" button is hidden. */
  currentContactId?: string;
}

/** Compact contact card (max ~360px) */
const ContactCard = ({
  contact,
  role,
  representativeType,
  isCurrent,
}: {
  contact: InvestmentEntityContact;
  role: string;
  representativeType?: string;
  isCurrent?: boolean;
}) => {
  const navigate = useNavigate();

  const fullName =
    contact.contact.name || `${contact.contact.firstName} ${contact.contact.lastName}`;

  return (
    <Box
      sx={{
        flex: '1 1 300px',
        minWidth: 260,
        maxWidth: 360,
      }}
    >
      <Card
        variant="outlined"
        sx={{
          mb: 1,
          backgroundColor: isCurrent ? 'rgba(25, 118, 210, 0.06)' : 'grey.50', // light primary tint or default
          borderColor: isCurrent ? 'primary.main' : 'divider',
          height: '100%',
        }}
      >
        <CardContent sx={{ p: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
            <StyledAvatar tone="tone1" sx={{ width: 28, height: 28 }}>
              <Person sx={{ fontSize: 16 }} />
            </StyledAvatar>

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  minWidth: 0,
                }}
              >
                <Typography
                  variant="body2"
                  fontWeight={600}
                  sx={{
                    lineHeight: 1.2,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                  title={fullName}
                >
                  {fullName}
                </Typography>
                {isCurrent && (
                  <Chip
                    label="Current"
                    size="small"
                    color="primary"
                    variant="outlined"
                    sx={{ height: 18, fontSize: '0.65rem' }}
                  />
                )}
              </Box>

              <Box
                sx={{
                  display: 'flex',
                  gap: 0.75,
                  alignItems: 'center',
                  mt: 0.5,
                  flexWrap: 'wrap',
                }}
              >
                <Chip label={role} size="small" color="primary" variant="outlined" />
                {representativeType && (
                  <Chip
                    label={representativeType}
                    size="small"
                    color="secondary"
                    variant="outlined"
                  />
                )}
              </Box>

              <Box sx={{ display: 'flex', gap: 1.5, mt: 0.75, flexWrap: 'wrap' }}>
                {contact.contact.email && (
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5,
                      minWidth: 0,
                    }}
                  >
                    <Email sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        maxWidth: 220,
                      }}
                      title={contact.contact.email}
                    >
                      {contact.contact.email}
                    </Typography>
                  </Box>
                )}
                {contact.contact.mobile && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Phone sx={{ fontSize: 14, color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      {contact.contact.mobile}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>

            {!isCurrent && (
              <Tooltip title="View Contact">
                <IconButton
                  size="small"
                  onClick={() => navigate(`/contacts/${contact.contact.id}`)}
                >
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export const ExpandableEntityCard: React.FC<ExpandableEntityCardProps> = ({
  entity,
  tone = 'default',
  expanded: controlledExpanded,
  onExpandedChange,
  contacts,
  contactsLoading,
  currentContactId,
}) => {
  const [internalExpanded, setInternalExpanded] = useState(false);
  const { navigate } = useNavigation();

  const isControlled = controlledExpanded !== undefined;
  const expanded = isControlled ? controlledExpanded : internalExpanded;

  const getEntityTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      INDIVIDUAL: 'Individual',
      TRUST: 'Trust',
      COMPANY: 'Company',
      PARTNERSHIP: 'Partnership',
      SMSF: 'SMSF',
      OTHER: 'Other',
    };
    return labels[type] || type;
  };

  const getEntityTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      INDIVIDUAL: '#2196F3',
      TRUST: '#4CAF50',
      COMPANY: '#FF9800',
      PARTNERSHIP: '#9C27B0',
      SMSF: '#F44336',
      OTHER: '#607D8B',
    };
    return colors[type] || '#607D8B';
  };

  const handleExpandClick = () => {
    const newExpanded = !expanded;
    if (isControlled) onExpandedChange?.(newExpanded);
    else setInternalExpanded(newExpanded);
  };

  const handleView = () => navigate(`/investment-entities/${entity.id}`);
  const handleEdit = () => navigate(`/investment-entities/${entity.id}/edit`);

  // reorder contacts to place current first (if provided)
  const orderedContacts = useMemo(() => {
    if (!contacts || !currentContactId) return contacts;
    const current = contacts.filter((c) => c.contact?.id === currentContactId);
    const others = contacts.filter((c) => c.contact?.id !== currentContactId);
    return [...current, ...others];
  }, [contacts, currentContactId]);

  return (
    <StyledCard tone={tone} sx={{ mb: 2 }}>
      <StyledCardContent>
        {/* Header Section */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <StyledAvatar
            tone={tone}
            sx={{
              width: 40,
              height: 40,
              backgroundColor: getEntityTypeColor(entity.type),
              color: 'white',
            }}
          >
            <Business />
          </StyledAvatar>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  pr: 1,
                }}
                title={entity.name || 'Unnamed Entity'}
              >
                {entity.name || 'Unnamed Entity'}
              </Typography>

              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="View Entity">
                  <IconButton size="small" onClick={handleView}>
                    <Visibility fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Edit Entity">
                  <IconButton size="small" onClick={handleEdit}>
                    <Edit fontSize="small" />
                  </IconButton>
                </Tooltip>
                <IconButton
                  onClick={handleExpandClick}
                  aria-expanded={expanded}
                  aria-label="show more"
                  size="small"
                >
                  {expanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 1, flexWrap: 'wrap' }}>
              <Chip
                label={getEntityTypeLabel(entity.type)}
                size="small"
                sx={{
                  backgroundColor: getEntityTypeColor(entity.type),
                  color: 'white',
                }}
              />
              {entity.isOwner && (
                <Chip label="Owner" size="small" color="success" variant="outlined" />
              )}
            </Box>

            {entity.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1, lineHeight: 1.4 }}>
                {entity.description}
              </Typography>
            )}

            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" color="text.secondary">
                Created: {new Date(entity.createdAt).toLocaleDateString()}
              </Typography>
            </Box>
          </Box>
        </Box>
      </StyledCardContent>

      {/* Expandable Content */}
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <StyledCardContent sx={{ borderTop: 1, borderColor: 'divider' }}>
          <Typography variant="h6" sx={{ mb: 1.5, fontWeight: 600 }}>
            Related Contacts
          </Typography>

          {contactsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : !orderedContacts || orderedContacts.length === 0 ? (
            <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
              No related contacts found.
            </Typography>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
              {orderedContacts.map((c) => (
                <ContactCard
                  key={c.id}
                  contact={c}
                  role={c.role}
                  representativeType={
                    c.representativeType === 'OTHER'
                      ? c.otherRepresentativeType
                      : c.representativeType
                  }
                  isCurrent={currentContactId === c.contact?.id}
                />
              ))}
            </Box>
          )}
        </StyledCardContent>
      </Collapse>
    </StyledCard>
  );
};
